<script lang="ts" setup>
/**
 * トークルーム投稿。
 */

import { defineProps, reactive, inject, toRefs } from "vue";
import TalkRoomController from "@/controllers/communityTalk/talkRoomController";
import { CommunityTalkStore, isValidCommunityTalkStoreKey } from "@/store/communityTalk/communityTalkStore";
import { ApiPostLoadStatus, ApiPostLoadStatusType } from "@/modules/types/apiLoadStatusType";
import LoginUtil from "@/modules/utils/loginUtil";
import PostPermissionUtil from "@/modules/utils/postPermissionUtil";
import ApiPostLoadMaster from "@/components/common/api/ApiPostLoadMaster.vue";
import RequireLogin from "@/components/communityTalk/RequireLogin.vue";
import PostComplete from "@/components/communityTalk/PostComplete.vue";
import TalkRoomPostNoticeMail from "@/components/communityTalk/talkRoom/TalkRoomPostNoticeMail.vue";
import AppConst from "@/modules/const";
import CustomTextarea from "@/components/common/form/CustomTextarea.vue";
import CustomLabel from "@/components/communityTalk/CustomLabel.vue";
import { ApiErrorViewModel, ApiValidationErrorViewModel } from "@/viewModels/apiErrorViewModel";
import PostNickName from "@/components/communityTalk/PostNickName.vue";
import PostMedia from "@/components/communityTalk/PostMedia.vue";
import PostProfilePicture from "@/components/communityTalk/PostProfilePicture.vue";
import PostReviewerAttribute from "@/components/communityTalk/PostReviewerAttribute.vue";
import { PostMediaViewModel } from "@/viewModels/communityTalk/postMediaViewModel";
import { LayoutTagGetResponse_WordSetting } from "@/apiRequests/models/communityTalk/layoutTagGetResponse";

interface State {
	/**
	 * トークルーム投稿内容。
	 */
	contents: string;
	/**
	 * トークルーム投稿内容。入力値が最大文字長を超えているかどうか。
	 */
	isOverContentsMaxLength: boolean;
	/**
	 * トークルーム投稿時のAPIステータス。
	 */
	status: ApiPostLoadStatusType;
	/**
	 * APIバリデーションエラーオブジェクト。
	 */
	apiValidationErrorViewModel: ApiValidationErrorViewModel | null;
	/**
	 * 作成されたトークルームID。
	 */
	postTalkRoomId: string;
	/**
	 * フォーム多重送信制御用フラグ
	 */
	isProcessing: boolean;
	/**
	 * 画像・動画を投稿中かどうか。
	 */
	isMediaPostProcessing: boolean;
	/**
	 * 内容が未入力かどうか。
	 */
	isContentsEmpty: boolean;
	/**
	 * ニックネームが未入力かどうか。
	 */
	isNickNameEmpty: boolean;
}
// reactiveな変数宣言
const state = reactive<State>({
	contents: "",
	isOverContentsMaxLength: false,
	status: ApiPostLoadStatus.beforeRequest,
	apiValidationErrorViewModel: null,
	postTalkRoomId: "",
	isProcessing: false,
	isMediaPostProcessing: false,
	isContentsEmpty: false,
	isNickNameEmpty: false,
});
// eslint-disable-next-line @typescript-eslint/no-unused-vars, no-undef
const { contents, isOverContentsMaxLength, status, apiValidationErrorViewModel, postTalkRoomId, isContentsEmpty, isNickNameEmpty } = toRefs(state);

interface Props {
	/**
	 * CommunityTalkStoreのInject用キー。
	 */
	communityTalkStoreKey: string;
	/**
	 * 表示タグ設定>文言設定。
	 */
	wordSettings: LayoutTagGetResponse_WordSetting;
}
const props = defineProps<Props>();

// Storeの初期化処理
if (!isValidCommunityTalkStoreKey(props.communityTalkStoreKey)) {
	// なんらかの原因でInjectKeyにエラーが発生していた場合は強制終了
	throw new Error(`InjectKeyにエラーが発生しました。${props.communityTalkStoreKey.toString()}`);
}
const communityTalkStore = inject(props.communityTalkStoreKey) as CommunityTalkStore;

const isPostable = PostPermissionUtil.isPostable(
	communityTalkStore.getGeneralSettings().talkRoomPostPermissionSetting,
	communityTalkStore.getGeneralSettings().talkRoomPostPermissionSpecificECMemberIds,
	communityTalkStore.getGeneralSettings().nonMemberIdList,
	communityTalkStore.getSiteUserId()
);
const isFormVisible = PostPermissionUtil.isFormVisible(
	communityTalkStore.getGeneralSettings().talkRoomPostPermissionSetting,
	communityTalkStore.getGeneralSettings().talkRoomPostPermissionSpecificECMemberIds,
	communityTalkStore.getSiteUserId()
);

// ニックネームが必須かどうか。
const isRequiredNickName = communityTalkStore.getGeneralSettings().nicknameRequiredSetting === "Required";

// トークルーム投稿内容入力欄変更時、閾値を超えているかどうかを判定
const onChangeContents = (): void => {
	if (state.contents.length > AppConst.TALKROOM_CONTENTS_MAX_LENGTH) {
		state.isOverContentsMaxLength = true;
	} else {
		state.isOverContentsMaxLength = false;
	}
};

// メディア投稿完了時の処理
const onSucessUploadMedias = (viewModels: PostMediaViewModel[]): void => {
	communityTalkStore.setTalkRoomPostMedia(viewModels);
};

// メディア投稿リセット時の処理
const onResetMedias = (): void => {
	communityTalkStore.resetTalkRoomPostMedia();
};

const talkRoomController = new TalkRoomController();
/**
 * トークルーム投稿処理。
 */
const post = async (): Promise<void> => {
	state.isProcessing = true;
	state.isNickNameEmpty = false;
	state.isContentsEmpty = false;

	// 内容が未入力の場合はエラーメッセージを表示
	if (state.contents === "") {
		state.isContentsEmpty = true;
		state.isProcessing = false;
	}
	// 内容が閾値を超えている場合何もしない
	if (state.isOverContentsMaxLength) {
		state.isProcessing = false;
	}
	// ニックネーム入力が必須かつ未入力の場合はエラーメッセージを表示
	if (isRequiredNickName && (communityTalkStore.getCommunityTalkUserNickName() == null || communityTalkStore.getCommunityTalkUserNickName() === "")) {
		state.isNickNameEmpty = true;
		state.isProcessing = false;
	}

	if (!state.isProcessing) return;

	state.status = ApiPostLoadStatus.loading;
	try {
		// SiteUserIdの送信可否を確認
		// 不可である場合、undefindを送信する。
		let siteUserId = communityTalkStore.getSiteUserId();
		if (!LoginUtil.canSendSiteUserId(siteUserId, communityTalkStore.getGeneralSettings().nonMemberIdList, communityTalkStore.getGeneralSettings().talkRoomPostPermissionSetting)) {
			siteUserId = undefined;
		}

		const talkRoomPostResponse = await talkRoomController.postTalkRoomData(
			communityTalkStore.getCommunityTalkUserNickName(),
			communityTalkStore.getCommunityTalkUserReviewerAttributes(),
			communityTalkStore.getCommunityTalkUserProfilePicViewModel().profilePicIdTmp,
			communityTalkStore.getTalkRoomPostMedia(),
			communityTalkStore.getTenantId(),
			communityTalkStore.getTagCd(),
			communityTalkStore.getSubjectCd(),
			communityTalkStore.getSubjectName(),
			communityTalkStore.getTalkRoomUrl(),
			state.contents,
			communityTalkStore.getReviCoCookieId(),
			siteUserId
		);
		state.postTalkRoomId = talkRoomPostResponse.talkRoomId;
		state.status = ApiPostLoadStatus.success;
		// LocalStorageにプリセット情報を保持
		communityTalkStore.setCommunityTalkViewModelToLocalStorage();
	} catch (e) {
		// バリデーションエラーであればエラーを表示させ、もう1度入力できるようにする
		if ((e as ApiErrorViewModel).isValidationError) {
			state.apiValidationErrorViewModel = (e as ApiErrorViewModel).validationErrorContents;
			state.status = ApiPostLoadStatus.beforeRequest;
		} else {
			// そうでなければエラー扱い(入力はもう一度出来ない)
			state.status = ApiPostLoadStatus.error;
		}
	}
	state.isProcessing = false;
};

// 画像・動画投稿開始イベント検知処理
const onStartMediaPostProcess = (): void => {
	state.isMediaPostProcessing = true;
};
// 画像・動画投稿終了イベント検知処理
const onEndMediaPostProcess = (): void => {
	state.isMediaPostProcessing = false;
};

// 投稿ボタンが有効かどうか
const isEnabledPostButton = (): boolean => {
	// 投稿が許可されてない場合は無効
	if (!isPostable) {
		return false;
	}
	// 投稿処理中なら無効
	if (state.isProcessing) {
		return false;
	}
	// 画像・動画投稿中なら無効
	if (state.isMediaPostProcessing) {
		return false;
	}
	return true;
};
</script>

<template>
	<api-post-load-master :status="status" v-if="isFormVisible">
		<template #beforeRequest>
			<section class="revico-community-talk-talkroom-post">
				<!-- EC会員のみ可能な投稿許可の場合、ログインしてなければ訴求文言表示 -->
				<template v-if="!isPostable">
					<require-login />
				</template>

				<!-- 投稿フォーム -->
				<!-- 内容 -->
				<custom-label class="revico-community-talk-post-contents--label" text="内容(必須)" :disabled="!isPostable" />
				<custom-textarea
					v-model="contents"
					@update:modelValue="onChangeContents()"
					name="contents"
					customClass="revico-community-talk-talkroom-post--contents"
					:api-validation-error-view-model="apiValidationErrorViewModel"
					:maxlength="10000"
					:disabled="!isPostable"
					:placeholder="wordSettings.talkRoomPostAreaDescription"
					:rows="5"
					:show-required-error="isContentsEmpty"
					label="内容"
				/>
				<span v-if="isOverContentsMaxLength" class="revico-community-talk-talkroom-post--contents-error-message">{{ AppConst.TALKROOM_CONTENTS_MAX_LENGTH }}文字以下で投稿してください。</span>

				<!-- 画像・動画投稿 -->
				<post-media
					:community-talk-store-key="communityTalkStoreKey"
					:disabled="!isPostable"
					post-type="TalkRoom"
					:init-post-media-view-models="communityTalkStore.getTalkRoomPostMedia()"
					@on-success-upload-medias="onSucessUploadMedias"
					@on-reset-medias="onResetMedias"
					@on-start-post-process="onStartMediaPostProcess"
					@on-end-post-process="onEndMediaPostProcess"
				/>

				<!-- ニックネーム -->
				<post-nick-name
					:community-talk-store-key="communityTalkStoreKey"
					post-type="TalkRoom"
					:disabled="!isPostable"
					:api-validation-error-view-model="apiValidationErrorViewModel"
					:show-required-error="isNickNameEmpty"
				/>
				<!-- プロフィール画像 -->
				<post-profile-picture :community-talk-store-key="communityTalkStoreKey" :disabled="!isPostable" :api-validation-error-view-model="apiValidationErrorViewModel" post-type="TalkRoom" />
				<!-- 投稿者プロフィール -->
				<PostReviewerAttribute :community-talk-store-key="communityTalkStoreKey" :disabled="!isPostable" :api-validation-error-view-model="apiValidationErrorViewModel" />
				<!-- 投稿ボタン -->
				<div class="revico-community-talk-talkroom-post--postbutton-wrapper">
					<button type="button" :disabled="!isEnabledPostButton()" @click="post" class="revico-community-talk-talkroom-post--postbutton">
						{{ wordSettings.talkRoomPostButtonDisplayName }}<font-awesome-icon icon="fa-solid fa-paper-plane" />
					</button>
				</div>
			</section>
		</template>
		<template #afterApiSuccess>
			<section class="revico-community-talk-talkroom-post">
				<post-complete>
					<template #contents>
						{{ wordSettings.talkRoomPostCompleteText }}
					</template>
				</post-complete>
				<p class="revico-community-talk-talkroom-post-complete--note">{{ wordSettings.reviewPostedAnnotationText }}</p>

				<talk-room-post-notice-mail :communityTalkStoreKey="communityTalkStoreKey" :talkRoomId="postTalkRoomId" />

				<p class="revico-community-talk-talkroom-post-complete--note">※続けて投稿したい場合は<br />画面を更新してください。</p>
			</section>
		</template>
	</api-post-load-master>
</template>

<style lang="scss">
.revico-widget .revico-community-talk .revico-community-talk {
	&-talkroom-post {
		margin: 20px 0;

		&--contents {
			&-error-message {
				display: inline-block;
				margin-top: 4px;
				color: var(--base-error-message-font-color);
				font-size: 0.9em;
			}
		}

		&--postbutton-wrapper {
			display: flex;
			justify-content: flex-end;
			width: 100%;
		}

		&--postbutton {
			@include communitytalk_postButton;
			@include communitytalk_postButton_disabled;

			.fa-paper-plane {
				margin-left: 4px;
			}
		}

		&-complete--note {
			margin-top: 12px;
			font-size: 0.8em;
			text-align: center;
			white-space: pre-line;
		}
	}
}
</style>
